import { useEffect, useRef, useState } from "react";
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

function rand(seed, offset = 0) {
  const x = Math.sin(seed * 9301 + offset * 49297 + 233) * 43758.5453;
  return x - Math.floor(x);
}
const COLS = 4, ROWS = 4, TOTAL = COLS * ROWS;
const IMAGE_SEEDS = ["ai1", "ai2", "ai3", "ai4", "ai5", "ai6", "ai7", "ai8", "ai9", "ai10", "ai11", "ai12", "ai13", "ai14", "ai15", "ai16"];
function shuffleZones() {
  const zones = [];
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) zones.push({ col: c, row: r });
  for (let i = zones.length - 1; i > 0; i--) { const j = Math.floor(rand(i, 7) * (i + 1));[zones[i], zones[j]] = [zones[j], zones[i]]; }
  return zones;
}
const ZONES = shuffleZones();
const PARTICLES = IMAGE_SEEDS.map((seed, i) => {
  const zone = ZONES[i];
  const cx = ((zone.col + 0.5 + (rand(i, 10) - 0.5) * 0.3) / COLS) * 100;
  const cy = ((zone.row + 0.5 + (rand(i, 11) - 0.5) * 0.3) / ROWS) * 100;
  return { src: `https://picsum.photos/seed/${seed}/600/600`, cx: Math.max(2, Math.min(98, cx)), cy: Math.max(2, Math.min(98, cy)), phase: i / TOTAL, maxSizeVw: 16 + rand(i, 4) * 4, baseOpacity: 0.45 + rand(i, 5) * 0.3, depth: 0.4 + rand(i, 12) * 0.6 };
});

const SERVICES = [
  { id: "01", title: "Business Intelligence & Dashboard Development", desc: "Build custom dashboards and reporting systems using tools such as Looker Studio, Tableau, Power BI, and GA4 to monitor business performance in real time." },
  { id: "02", title: "Customer Journey & Funnel Analytics", desc: "Identify bottlenecks across acquisition, conversion, retention, and customer lifecycle stages to improve overall marketing efficiency and profitability." },
  { id: "03", title: "Marketing Analytics & Attribution", desc: "Gain deeper visibility into campaign performance, customer acquisition costs, channel effectiveness, and return on advertising spend." },
  { id: "04", title: "AI Workflow Automation", desc: "Automate repetitive business processes, reporting tasks, lead qualification workflows, customer communication systems, and operational processes using modern AI tools." },
  { id: "05", title: "Data Strategy & Performance Audits", desc: "Evaluate existing data infrastructure, tracking systems, analytics implementation, and reporting processes to uncover growth opportunities and improve decision-making." },
  { id: "06", title: "Predictive Insights & Growth Intelligence", desc: "Leverage data analysis and AI-powered insights to identify trends, forecast opportunities, and support strategic business planning." },
];

function GridBg() {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 28px,rgba(236,43,40,0.04) 28px,rgba(236,43,40,0.04) 29px)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(90deg,transparent,transparent 79px,rgba(236,43,40,0.025) 79px,rgba(236,43,40,0.025) 80px)" }} />
      <div style={{ position: "absolute", top: 0, right: 0, width: 320, height: 320, background: "radial-gradient(circle at top right,rgba(236,43,40,0.06) 0%,transparent 65%)" }} />
    </div>
  );
}

// ─── SERVICES — internal infinite scroll, color gradient by position ──────────
const SCROLL_SPEED = 0.6;

function ServicesSection() {
  const [isMobile, setIsMobile] = useState(false);
  const [clickedIdx, setClickedIdx] = useState(null); // which item shows desc
  const rightRef = useRef(null);
  const innerRef = useRef(null);
  const rafRef = useRef(null);
  const posRef = useRef(0);
  const setHRef = useRef(0);
  const [opacities, setOpacities] = useState(SERVICES.map(() => 0.15));

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Measure one set height
  useEffect(() => {
    const measure = () => {
      const inner = innerRef.current;
      if (!inner) return;
      setHRef.current = inner.scrollHeight / 3;
      posRef.current = setHRef.current;
      if (rightRef.current) rightRef.current.scrollTop = posRef.current;
    };
    setTimeout(measure, 50);
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isMobile]);

  // Compute per-item opacity: TOP item = 1 (black), fades toward bottom
  const computeOpacities = () => {
    const container = rightRef.current;
    if (!container) return;
    const containerH = container.clientHeight;
    const containerTop = container.getBoundingClientRect().top;
    // Only measure first set items
    const items = container.querySelectorAll(".srv-item-first");
    const newOps = Array(SERVICES.length).fill(0.08);
    items.forEach((el, idx) => {
      const r = el.getBoundingClientRect();
      const itemTop = r.top - containerTop;
      // 0 at top of container, 1 at bottom
      const posRatio = itemTop / containerH;
      // Clamp: top=0 → opacity 1.0, posRatio=0.5 → 0.45, posRatio=1 → 0.08
      const op = Math.max(0.08, Math.min(1, 1 - posRatio * 1.7));
      newOps[idx % SERVICES.length] = op;
    });
    setOpacities(newOps);
  };

  // RAF: scroll right div, update opacities every frame — runs on ALL devices
  useEffect(() => {
    const tick = () => {
      if (rightRef.current && setHRef.current > 0) {
        posRef.current += SCROLL_SPEED;
        if (posRef.current >= setHRef.current * 2) {
          posRef.current -= setHRef.current;
        }
        rightRef.current.scrollTop = posRef.current;
        computeOpacities();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isMobile]);

  const handleClick = (realIdx) => {
    setClickedIdx(prev => prev === realIdx ? null : realIdx);
  };

  const tripled = [...SERVICES, ...SERVICES, ...SERVICES];

  return (
    <section style={{ background: "#fff", borderTop: "1px solid #e5e5e5" }}>

      {/* Tag row */}
      <div style={{ padding: "18px clamp(24px,5vw,72px)", borderBottom: "1px solid #e5e5e5", fontFamily: "'Montserrat',sans-serif", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#bbb" }}>
        What We Do
      </div>

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}>

        {/* LEFT sticky — desktop only */}
        {!isMobile && (
          <div style={{ width: "38%", flexShrink: 0, position: "sticky", top: 0, height: "100vh", borderRight: "1px solid #e5e5e5", display: "flex", flexDirection: "column", justifyContent: "center", padding: "clamp(48px,6vw,88px)" }}>
            <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "#bbb", marginBottom: 20 }}>Our Services</p>
            <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "clamp(2.2rem,4vw,5rem)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1, color: "#111", marginBottom: 48 }}>
              Data &amp; AI<br />solutions.
            </h2>
            <a href="#contact"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 32px", background: "#111", color: "#fff", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none", borderRadius: 40, width: "fit-content", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#ec2b28"}
              onMouseLeave={e => e.currentTarget.style.background = "#111"}>
              Get started →
            </a>
          </div>
        )}

        {/* RIGHT — overflow hidden, RAF scrolls internally */}
        <div
          ref={rightRef}
          style={{
            flex: 1,
            height: "100vh",
            overflowY: "hidden",
            position: "sticky",
            top: 0,
            /* Fade mask: strong at top, transparent at bottom */
            WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 35%, transparent 100%)",
            maskImage: "linear-gradient(to bottom, black 0%, black 35%, transparent 100%)",
          }}
        >
          <div ref={innerRef}>

            {/* Mobile header */}
            {isMobile && (
              <div style={{ padding: "28px 20px 20px", borderBottom: "1px solid #e5e5e5" }}>
                <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: "clamp(1.8rem,7vw,2.8rem)", fontWeight: 800, letterSpacing: "-0.03em", color: "#111", marginBottom: 16 }}>
                  Data &amp; AI solutions.
                </h2>
              </div>
            )}

            {/* Items */}
            {tripled.map((s, i) => {
              const realIdx = i % SERVICES.length;
              const isFirstSet = !isMobile ? (i < SERVICES.length) : true;
              const op = isMobile ? 1 : (opacities[realIdx] ?? 0.15);
              const isClicked = clickedIdx === realIdx;

              return (
                <div
                  key={i}
                  className={isFirstSet ? "srv-item-first" : ""}
                  onClick={() => handleClick(realIdx)}
                  style={{
                    borderBottom: "1px solid #e5e5e5",
                    padding: isMobile
                      ? "clamp(18px,4vw,28px) 20px"
                      : "clamp(22px,3vw,40px) clamp(32px,5vw,64px)",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "background 0.2s",
                    background: isClicked ? "#fafafa" : "transparent",
                  }}
                >
                  {/* Title row */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                    <span style={{
                      fontFamily: "'Montserrat',sans-serif",
                      fontSize: isMobile
                        ? "clamp(1rem,4.5vw,1.3rem)"
                        : "clamp(1.1rem,2vw,2.4rem)",
                      fontWeight: 500,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.2,
                      color: `rgba(17,17,17,${isMobile ? 1 : op})`,
                      transition: "color 0.1s",
                      display: "block",
                      // Underline on hover/active
                      textDecoration: isClicked ? "underline" : "none",
                      textUnderlineOffset: 6,
                      textDecorationThickness: 1,
                    }}>
                      {s.title}
                    </span>
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: `rgba(180,180,180,${isMobile ? 1 : op})`, letterSpacing: "0.14em", flexShrink: 0 }}>
                      {s.id}
                    </span>
                  </div>

                  {/* Description — only when clicked */}
                  {isClicked && (
                    <p style={{
                      fontFamily: "'Montserrat',sans-serif",
                      fontSize: "clamp(0.8rem,0.9vw,0.9rem)",
                      lineHeight: 1.8,
                      color: "#555",
                      fontWeight: 400,
                      marginTop: 12,
                      maxWidth: 560,
                    }}>
                      {s.desc}
                    </p>
                  )}
                </div>
              );
            })}

            {/* Mobile CTA */}
            {isMobile && (
              <div style={{ padding: "28px 20px" }}>
                <a href="#contact"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 32px", background: "#111", color: "#fff", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none", borderRadius: 40 }}>
                  Get started →
                </a>
              </div>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}


// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function MergedPage() {
  const ctaRef = useRef(null);
  const particleRefs = useRef([]);
  const rafRef = useRef(null);
  const pausedRef = useRef(null);
  const pausedTRef = useRef({});
  const accumulatedRef = useRef(0);
  const lastNowRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const smoothMouseRef = useRef({ x: 0.5, y: 0.5 });
  const smoothSizeRef = useRef(PARTICLES.map(p => p.maxSizeVw * (p.phase * p.phase)));
  const smoothOpacityRef = useRef(PARTICLES.map(() => 0));
  const cursorRef = useRef(null);

  useEffect(() => {
    lastNowRef.current = performance.now();
    const onMM = e => { mouseRef.current = { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }; };
    const onTM = e => { if (e.touches.length > 0) mouseRef.current = { x: e.touches[0].clientX / window.innerWidth, y: e.touches[0].clientY / window.innerHeight }; };
    window.addEventListener("mousemove", onMM);
    window.addEventListener("touchmove", onTM, { passive: true });
    const cls = [() => window.removeEventListener("mousemove", onMM), () => window.removeEventListener("touchmove", onTM)];
    PARTICLES.forEach((_, i) => {
      const el = particleRefs.current[i]; if (!el) return;
      const en = () => { pausedRef.current = i; }, le = () => { pausedRef.current = null; };
      el.addEventListener("mouseenter", en); el.addEventListener("mouseleave", le);
      el.style.pointerEvents = "all"; el.style.cursor = "pointer";
      cls.push(() => { el.removeEventListener("mouseenter", en); el.removeEventListener("mouseleave", le); });
    });
    const tick = now => {
      const delta = (now - lastNowRef.current) / 1000; lastNowRef.current = now;
      if (pausedRef.current === null) accumulatedRef.current += delta;
      const elapsed = accumulatedRef.current;
      const isMob = window.innerWidth <= 768;
      const sm = smoothMouseRef.current, tm = mouseRef.current;
      sm.x += (tm.x - sm.x) * (isMob ? 0.1 : 0.05); sm.y += (tm.y - sm.y) * (isMob ? 0.1 : 0.05);
      const mx = sm.x - 0.5, my = sm.y - 0.5;
      PARTICLES.forEach((p, i) => {
        const el = particleRefs.current[i]; if (!el) return;
        const isH = pausedRef.current === i, anyH = pausedRef.current !== null;
        let t;
        if (isH) { if (pausedTRef.current[i] === undefined) pausedTRef.current[i] = (elapsed / 5 + p.phase) % 1; t = pausedTRef.current[i]; }
        else { pausedTRef.current[i] = undefined; t = (elapsed / 5 + p.phase) % 1; }
        const tSz = p.maxSizeVw * (t * t) * (isMob ? 1.7 : 1);
        let tOp = p.baseOpacity;
        if (t < 0.12) tOp *= t / 0.12; if (t > 0.78) tOp *= (1 - t) / 0.22;
        if (anyH && !isH) tOp = 0.06; if (isH) tOp = 1;
        smoothSizeRef.current[i] += (tSz - smoothSizeRef.current[i]) * 0.055;
        smoothOpacityRef.current[i] += (tOp - smoothOpacityRef.current[i]) * (isH || anyH ? 0.12 : 0.045);
        el.style.width = `${smoothSizeRef.current[i]}vw`;
        el.style.height = `${smoothSizeRef.current[i]}vw`;
        el.style.opacity = smoothOpacityRef.current[i];
        el.style.zIndex = isH ? 10 : 1; el.style.transition = "none";
        el.style.transform = `translate(calc(-50% + ${mx * 70 * p.depth}px),calc(-50% + ${my * 70 * p.depth}px))`;
      });
      if (ctaRef.current) {
        const s = Math.max(0.75, Math.min(1, 1 - window.scrollY / (window.innerHeight * 1.2)));
        ctaRef.current.style.transform = `scale(${s})`;
        ctaRef.current.style.opacity = Math.max(0, Math.min(1, s * 1.5 - 0.1));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(rafRef.current); cls.forEach(f => f()); };
  }, []);

  useEffect(() => {
    const mv = e => { if (cursorRef.current) { cursorRef.current.style.left = e.clientX + "px"; cursorRef.current.style.top = e.clientY + "px"; } };
    window.addEventListener("mousemove", mv);
    return () => window.removeEventListener("mousemove", mv);
  }, []);

  return (
    <>
      <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  :root{
    --bg:#0a0a0a;
    --bg2:#111;
    --red:#ec2b28;
    --text:#e8e8e8;
    --muted:#666;
    --border:rgba(236,43,40,0.14);
    --font-family:'Inter',sans-serif;
  }
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;font-family:var(--font-family)!important;}
  body{font-family:var(--font-family);}

        .ai-cursor{position:fixed;width:14px;height:14px;border:1.5px solid #ec2b28;border-radius:50%;pointer-events:none;z-index:9999;transform:translate(-50%,-50%);mix-blend-mode:screen;}
        @media(max-width:768px){.ai-cursor{display:none;}}

        .wg-sticky{position:sticky;top:0;height:100vh;z-index:1;border-left:1px solid var(--border);border-right:1px solid var(--border);}
.cta-wrap{text-align:center;padding:clamp(60px,9vw,120px) clamp(24px,6vw,100px);position:relative;overflow:hidden;border-left:1px solid var(--border);border-right:1px solid var(--border);}
        .wg-stage{position:relative;width:100%;height:100%;overflow:hidden;background:#fff;}
        .wg-particle{position:absolute;overflow:hidden;pointer-events:none;will-change:transform,width,height,opacity;border-radius:3px;transform:translate(-50%,-50%);}
        .wg-particle img{width:100%;height:100%;object-fit:cover;display:block;}
        .wg-center{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:20;pointer-events:none;}
        .wg-cta{font-family:var(--font-family);font-size:clamp(2.4rem,6vw,7rem);font-weight:900;letter-spacing:-0.04em;color:#111;text-decoration:none;pointer-events:all;transform-origin:center;will-change:transform,opacity;mix-blend-mode:multiply;}
        .wg-next{position:relative;z-index:2;background:var(--bg);}

        .ticker{overflow:hidden;border-top:1px solid var(--border);border-bottom:1px solid var(--border);padding:13px 0;background:var(--bg2);}
        .ticker-inner{display:flex;gap:56px;width:max-content;animation:tick 28s linear infinite;}
        @keyframes tick{from{transform:translateX(0);}to{transform:translateX(-50%);}}
        .ticker-item{font-family:var(--font-family);font-size:10px;letter-spacing:0.2em;text-transform:uppercase;color:var(--muted);white-space:nowrap;display:flex;align-items:center;gap:18px;}
        .ticker-item::before{content:'◆';color:var(--red);font-size:6px;}

        .sec{padding:clamp(60px,9vw,120px) clamp(24px,6vw,100px);position:relative;}
        .sec-label{font-family:var(--font-family);font-size:10px;letter-spacing:0.25em;text-transform:uppercase;color:var(--red);margin-bottom:48px;display:flex;align-items:center;gap:16px;}
        .sec-label::after{content:'';flex:1;height:1px;background:var(--border);}

        .ov-grid{display:grid;grid-template-columns:1fr 1fr;gap:clamp(40px,5vw,80px);align-items:start;}
        @media(max-width:768px){.ov-grid{grid-template-columns:1fr;gap:32px;}}
        .ov-h{font-family:var(--font-family);font-size:clamp(1.7rem,3.2vw,3.2rem);font-weight:800;line-height:1.1;letter-spacing:-0.025em;color:var(--text);}
        .ov-h .red{color:var(--red);}
        .ov-body{font-family:var(--font-family);font-size:clamp(0.82rem,1vw,0.95rem);line-height:1.9;color:var(--muted);font-weight:400;}
        .ov-body p+p{margin-top:18px;}

        .cta-wrap{text-align:center;padding:clamp(60px,9vw,120px) clamp(24px,6vw,100px);position:relative;overflow:hidden;}
        .cta-wrap::before{content:'';position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:560px;height:560px;background:radial-gradient(circle,rgba(236,43,40,0.07) 0%,transparent 65%);pointer-events:none;}
        .cta-h{font-family:var(--font-family);font-size:clamp(1.8rem,4vw,4.5rem);font-weight:900;letter-spacing:-0.03em;line-height:1.1;margin-bottom:18px;color:var(--text);}
        .cta-sub{font-family:var(--font-family);font-size:clamp(0.82rem,1vw,0.95rem);color:var(--muted);max-width:460px;margin:0 auto 40px;line-height:1.85;font-weight:400;}
        .ai-btn{font-family:var(--font-family);font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;padding:15px 36px;background:var(--red);color:#fff;border:none;cursor:pointer;clip-path:polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,10px 100%,0 calc(100% - 10px));transition:opacity .2s,transform .15s;text-decoration:none;display:inline-block;}
        .ai-btn:hover{opacity:.85;transform:translateY(-1px);}
      `}</style>

      <div ref={cursorRef} className="ai-cursor" />
      <Navbar />

      <div style={{ position: "relative" }}>
        {/* HERO */}
        <div className="wg-sticky">
          <div className="wg-stage">
            {PARTICLES.map((p, i) => (
              <div key={i} ref={el => particleRefs.current[i] = el} className="wg-particle"
                style={{ left: `${p.cx}%`, top: `${p.cy}%`, width: `${p.maxSizeVw * (p.phase * p.phase)}vw`, height: `${p.maxSizeVw * (p.phase * p.phase)}vw`, opacity: 0, zIndex: 1 }}>
                <img src={p.src} alt="" loading="lazy" />
              </div>
            ))}
            <div className="wg-center">
              <a ref={ctaRef} href="#overview" className="wg-cta">Aurelix Labs</a>
            </div>
          </div>
        </div>

        <div className="wg-next">
          {/* ticker */}
          <div className="ticker">
            <div className="ticker-inner">
              {["Business Intelligence", "Funnel Analytics", "AI Automation", "Marketing Attribution", "Data Strategy", "Predictive Insights", "Growth Intelligence", "Dashboard Development",
                "Business Intelligence", "Funnel Analytics", "AI Automation", "Marketing Attribution", "Data Strategy", "Predictive Insights", "Growth Intelligence", "Dashboard Development"]
                .map((t, i) => <span key={i} className="ticker-item">{t}</span>)}
            </div>
          </div>

          {/* overview */}
          <section className="sec" id="overview" style={{ background: "var(--bg)" }}>
            <GridBg />
            <p className="sec-label">Overview</p>
            <div className="ov-grid">
              <h2 className="ov-h">Most businesses drown<br />in data. We turn it into<br /><span className="red">your edge.</span></h2>
              <div className="ov-body">
                <p>Modern businesses generate massive amounts of data every day, but most organizations struggle to convert that information into actionable insights.</p>
                <p>We help companies build analytics frameworks, reporting systems, customer intelligence dashboards, and AI-powered workflows that provide clarity, improve decision-making, and unlock growth opportunities.</p>
                <p>Whether you need visibility into marketing performance, customer behavior, operational efficiency, or scalable automation — our team combines analytics expertise with practical AI implementation tailored to your business.</p>
              </div>
            </div>
          </section>

          {/* services */}
          <ServicesSection />

          {/* cta */}
          <section className="cta-wrap" style={{ background: "var(--bg)" }}>
            <GridBg />
            <div style={{ position: "relative", zIndex: 1 }}>
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--red)", marginBottom: 22 }}>Ready to start?</p>
              <h2 className="cta-h">Faster. Smarter.<br /><span style={{ color: "var(--red)" }}>More profitable.</span></h2>
              <p className="cta-sub">Our goal is simple: help businesses make faster, smarter, and more profitable decisions through data, analytics, and intelligent automation.</p>
              <a href="#contact" className="ai-btn">Start the Conversation</a>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </>
  );
}